/**
 * useTenantRoster — fetches + owns the Tenants roster (WSRPDM-0012).
 *
 * `globalEnabled` is duplicated on every roster row (contract §1.3 — "read on
 * any row's `globalEnabled`, no standalone GET"); this hook surfaces it once,
 * derived from the first row. None of the mutation endpoints return updated
 * state (contract §1.4), so `refetch()` re-pulls the whole roster after any
 * write — callers invoke it after a successful toggle.
 *
 * `error` is an error-KIND, not a pre-translated string — translate it at the
 * call site (mirrors `TenantDetailPanel`'s `LoadError` pattern). Translating
 * inside the effect would need `useCopy()`'s `t` in the dependency array, and
 * `t` is a fresh function reference every render (not memoized) — with
 * `setTenants(res.tenants)` also handing back a fresh array every fetch, that
 * combination re-triggers the effect on every render, forever (an unbounded
 * refetch loop QA caught in review).
 */
import { useCallback, useEffect, useState } from 'react';
import { listTenants, setGlobalEnabled } from '@/api/wsr/tenants';
import type { PlatformTenantSummary } from '@/api/wsr/types';

export type RosterLoadError = 'load-failed' | null;

export interface UseTenantRoster {
  tenants: PlatformTenantSummary[] | null;
  loading: boolean;
  error: RosterLoadError;
  refetch: () => void;
  /** null until the roster has loaded at least one row. */
  globalEnabled: boolean | null;
  globalToggling: boolean;
  /** Throws on failure — the caller surfaces the error inline. */
  toggleGlobal: (enabled: boolean) => Promise<void>;
}

export function useTenantRoster(): UseTenantRoster {
  const [tenants, setTenants] = useState<PlatformTenantSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RosterLoadError>(null);
  const [globalToggling, setGlobalToggling] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listTenants()
      .then((res) => {
        if (cancelled) return;
        setTenants(res.tenants);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError('load-failed');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  // Setting loading/error here (an event-handler-style callback, not the
  // effect body itself) ahead of bumping reloadToken is what re-arms the
  // loading UI for a refetch without tripping react-hooks/set-state-in-effect.
  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    setReloadToken((n) => n + 1);
  }, []);

  const toggleGlobal = useCallback(
    async (enabled: boolean) => {
      setGlobalToggling(true);
      try {
        await setGlobalEnabled(enabled);
        refetch();
      } finally {
        setGlobalToggling(false);
      }
    },
    [refetch],
  );

  const globalEnabled = tenants && tenants.length > 0 ? (tenants[0]?.globalEnabled ?? null) : null;

  return { tenants, loading, error, refetch, globalEnabled, globalToggling, toggleGlobal };
}
